import i18n from 'i18next'
import { toast } from 'sonner'
import {
  publishAudio, publishAudioPlaylist, publishStopScreen, getScreenGroups,
  publishPlaylist, publishSignageLayout, type Mix,
} from '@meno/ui/lib/api'
import { publishTransition } from '@meno/ui/lib/api'
import type { CompanionFields } from '@/components/companionsModel'
import type { PublishPayload } from '@/components/PublishDialog'

// ── Companion co-publish ────────────────────────────────────────────────────
// A playlist / layout can carry companion picks (audio source, stop screen,
// transition) chosen at creation time. Publishing composes the existing
// per-kind publish endpoints with the SAME screens/groups/window payload, so
// everything lands together and each kind still gets its own Task Center
// entry. Transitions are frontend-only (localStorage per screen), so they're
// applied here after resolving group members to screen ids.

// screen_ids ∪ (member screens of group_ids) — needed only for the
// transition, which has no backend and must be stamped per screen. Also used
// by the Dashboard's Quick Publish transition flow.
export async function resolveScreenIds(payload: PublishPayload): Promise<number[]> {
  const ids = new Set<number>(payload.screen_ids)
  if (payload.group_ids.length > 0) {
    try {
      const { data } = await getScreenGroups()
      for (const g of data || []) {
        if (!payload.group_ids.includes(g.id)) continue
        for (const s of g.screens || []) ids.add(s.id)
      }
    } catch { /* unresolvable groups just skip the transition stamp */ }
  }
  return [...ids]
}

type Job = { label: string; run: () => Promise<unknown> }

function companionJobs(entity: CompanionFields, payload: PublishPayload): Job[] {
  const jobs: Job[] = []
  if (entity.companion_audio_id != null) {
    jobs.push({
      label: i18n.t('companions.audio', 'Audio'),
      run: () => publishAudio(entity.companion_audio_id!, payload),
    })
  } else if (entity.companion_audio_playlist_id != null) {
    jobs.push({
      label: i18n.t('companions.audio', 'Audio'),
      run: () => publishAudioPlaylist(entity.companion_audio_playlist_id!, payload),
    })
  }
  if (entity.companion_stop_screen_id != null) {
    jobs.push({
      label: i18n.t('companions.stopScreen', 'Stop screen'),
      run: () => publishStopScreen(entity.companion_stop_screen_id!, payload),
    })
  }
  if (entity.companion_transition) {
    jobs.push({
      label: i18n.t('companions.transition', 'Transition'),
      run: () => publishTransition({
        key: entity.companion_transition!,
        screen_ids: payload.screen_ids,
        group_ids: payload.group_ids,
      }),
    })
  }
  return jobs
}

// Fire every job in parallel. Never throws — the main publish already
// succeeded, so partial failures surface as one warning toast instead of
// failing the whole dialog.
async function runJobs(jobs: Job[]): Promise<void> {
  if (jobs.length === 0) return
  const results = await Promise.allSettled(jobs.map(j => j.run()))
  const failed = jobs.filter((_, i) => results[i].status === 'rejected').map(j => j.label)
  if (failed.length > 0) {
    toast.warning(i18n.t('companions.partialFail', {
      defaultValue: 'Published, but lab content failed: {{items}}',
      items: failed.join(', '),
    }))
  }
}

// Fire every companion publish in parallel. Never throws — the main publish
// already succeeded, so partial companion failures surface as one warning
// toast instead of failing the whole dialog.
export async function publishCompanions(
  entity: CompanionFields | null | undefined,
  payload: PublishPayload,
): Promise<void> {
  if (!entity) return
  await runJobs(companionJobs(entity, payload))
}

// Publish a Mix directly: its main content (playlist OR layout, whichever is
// set) plus its companions, all against the same screens/window. Unlike
// withCompanions (which co-publishes alongside an ALREADY-published playlist/
// layout's own inline picks), a mix has no separate "main publish" endpoint of
// its own — this IS the publish.
export async function publishMix(mix: Mix, payload: PublishPayload): Promise<void> {
  const jobs: Job[] = []
  if (mix.playlist_id != null) {
    jobs.push({
      label: mix.playlist_name ?? i18n.t('nav.media', 'Playlist'),
      run: () => publishPlaylist(mix.playlist_id!, payload),
    })
  } else if (mix.layout_id != null) {
    jobs.push({
      label: mix.layout_name ?? i18n.t('nav.layouts', 'Layout'),
      run: () => publishSignageLayout(mix.layout_id!, payload),
    })
  }
  jobs.push(...companionJobs({
    companion_audio_id: mix.audio_id,
    companion_audio_playlist_id: mix.audio_playlist_id,
    companion_stop_screen_id: mix.stop_screen_id,
    companion_transition: mix.transition,
  }, payload))
  await runJobs(jobs)
}

// Wrap a PublishDialog `publish` callback so the companions carried on the
// publish response (PlaylistOut / SignageLayoutOut include companion_*) get
// co-published to the same targets right after the main publish succeeds.
export function withCompanions<TResp extends CompanionFields>(
  publish: (payload: PublishPayload) => Promise<{ data: TResp }>,
): (payload: PublishPayload) => Promise<{ data: TResp }> {
  return async (payload: PublishPayload) => {
    const res = await publish(payload)
    await publishCompanions(res.data, payload)
    return res
  }
}
